import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAppStore } from '../store/appStore';
import { useGoogleAuth } from '../store/googleAuthStore';
import {
  filterVideo,
  formatIso8601Duration,
  getWatchedVideoIds,
  getWatchProgressByVideoId,
  orderVideosByFreshnessThenWatchStatus,
  parseIso8601DurationSeconds,
  type Video,
} from '@kidstube/shared';
import { applyFallbackThumbnail } from '../utils/thumbnailFallback';
import './PlayerPage.css';

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}

type SuggestionDirection = 'next' | 'previous';
type WatchState = 'new' | 'continue' | 'watched';
type ServerPlaybackVerdict = 'checking' | 'approved' | 'blocked' | 'retryable';
const AUTO_ADVANCE_DELAY_MS = 3000;

interface CloudWatchBatch {
  eventId: string;
  payload: Record<string, unknown>;
}

function createWatchEventId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
}

function selectAdjacentVideo(
  videos: readonly Video[],
  activeVideoId: string,
  watchedVideoIds: Set<string>,
  direction: SuggestionDirection
): Video | null {
  const candidates = videos.filter((video) => video.id !== activeVideoId);
  const freshCandidates = candidates.filter((video) => !watchedVideoIds.has(video.id));
  const queue = freshCandidates.length > 0 ? freshCandidates : candidates;

  if (queue.length === 0) {
    return null;
  }

  return direction === 'previous' ? queue[queue.length - 1] : queue[0];
}

function getVideoWatchState(
  videoId: string,
  progressByVideoId: ReturnType<typeof getWatchProgressByVideoId>
): WatchState {
  const progress = progressByVideoId.get(videoId);

  if (progress?.completed) return 'watched';
  if ((progress?.watchedDurationSeconds || 0) > 5) return 'continue';
  return 'new';
}

export function PlayerPage() {
  const { videoId: routeVideoId } = useParams<{ videoId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Get channel from navigation state (passed from VideoCard)
  const navState = location.state as {
    requestFullscreen?: boolean;
    video?: Video;
    channel?: { id: string; title: string };
    returnTo?: string;
  } | null;
  const returnToRef = useRef(navState?.returnTo || '/');
  const initialCachedVideo: Video | null =
    navState?.video?.id === routeVideoId ? navState?.video ?? null : null;

  // Video state management
  const videoIdRef = useRef<string>(routeVideoId || '');
  const [videoIdForFetch, setVideoIdForFetch] = useState<string | null>(null);

  const [relatedSource, setRelatedSource] = useState<Video[]>([]);
  const [relatedLoading, setRelatedLoading] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<Video | null>(initialCachedVideo);
  const [serverApprovedVideo, setServerApprovedVideo] = useState<Video | null>(initialCachedVideo);
  const [serverPlaybackVerdict, setServerPlaybackVerdict] =
    useState<ServerPlaybackVerdict>(initialCachedVideo ? 'approved' : 'checking');
  const [playbackIssueReason, setPlaybackIssueReason] = useState<string | null>(null);
  const [, setCurrentIndex] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  // Initialize currentChannel from navigation state if available
  const [currentChannel, setCurrentChannel] = useState<{ id: string; title: string } | null>(() => {
    console.log('[DEBUG] Initial currentChannel from nav state:', navState?.channel);
    return navState?.channel || null;
  });
  const [showTapToStart, setShowTapToStart] = useState(() => {
    // Only show tap-to-start on iOS (Safari autoplay restrictions)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    // Also always show on PWA mode (standalone) for iOS since autoplay is stricter
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    return isIOS || (isPWA && isIOS);
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState({ current: 0, duration: 0 });

  // Refs
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const playerRef = useRef<any>(null);
  const loadedVideoIdRef = useRef<string | null>(null);
  const currentVideoRef = useRef<Video | null>(currentVideo);
  const hasUnmutedRef = useRef<boolean>(false);
  const isInitialLoadRef = useRef<boolean>(true); // Track if this is first load
  const isDraggingRef = useRef<boolean>(false); // Track if user is dragging the related videos strip
  const watchedThisPlayerSessionRef = useRef<Set<string>>(new Set());
  const lastWatchTickAtRef = useRef<number | null>(null);
  const pendingCloudWatchSecondsRef = useRef<Map<string, number>>(new Map());
  const inFlightCloudWatchRef = useRef<Map<string, CloudWatchBatch>>(new Map());
  const sendingCloudWatchRef = useRef<Set<string>>(new Set());
  const flushCloudWatchEventRef = useRef<(videoId: string) => void>(() => {});
  const relatedRef = useRef<Video[]>([]);
  const watchedVideoIdsRef = useRef<Set<string>>(new Set());
  const autoAdvanceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const switchToVideoInternalRef = useRef<
    (video: Video, newIndex?: number, options?: { saveCurrentPosition?: boolean }) => void
  >(() => {});

  const {
    addToWatchHistory,
    updateWatchHistoryDuration,
    addDiscoveredVideos,
    isTimeUp,
    addToAllowlist,
    addToDenylist,
    denylist,
    discoveredVideos,
    allowlist,
    watchHistory,
    currentProfile,
    playbackResume,
    savePlaybackPosition,
    clearPlaybackPosition,
  } = useAppStore();
  const { isSignedIn } = useGoogleAuth();

  const playbackResumeRef = useRef(playbackResume);
  const watchHistoryRef = useRef(watchHistory);

  useEffect(() => {
    playbackResumeRef.current = playbackResume;
  }, [playbackResume]);

  useEffect(() => {
    watchHistoryRef.current = watchHistory;
  }, [watchHistory]);

  useEffect(() => {
    currentVideoRef.current = currentVideo;
  }, [currentVideo]);

  const watchedVideoIds = useMemo(() => getWatchedVideoIds(watchHistory), [watchHistory]);
  const progressByVideoId = useMemo(() => getWatchProgressByVideoId(watchHistory), [watchHistory]);

  const related = useMemo(() => {
    const blockedIds = new Set(denylist.map((e) => e.id));
    const allowedChannelIds = new Set(
      allowlist.filter((e) => e.type === 'channel').map((e) => e.id)
    );

    const favorites: Video[] = [];
    const others: Video[] = [];
    const seenVideoIds = new Set<string>();

    for (const video of [...relatedSource, ...discoveredVideos]) {
      if (seenVideoIds.has(video.id)) continue;
      seenVideoIds.add(video.id);
      if (blockedIds.has(video.id) || blockedIds.has(video.snippet.channelId)) continue;
      if (!filterVideo(video, { allowlist, denylist, requireMadeForKids: true }).allowed) continue;

      if (allowedChannelIds.has(video.snippet.channelId)) {
        favorites.push(video);
      } else {
        others.push(video);
      }
    }

    return orderVideosByFreshnessThenWatchStatus([...favorites, ...others], watchedVideoIds);
  }, [allowlist, denylist, discoveredVideos, relatedSource, watchedVideoIds]);

  const activeVideoId = currentVideo?.id || videoIdRef.current;
  const visibleRelated = useMemo(
    () => related.filter((video) => video.id !== activeVideoId),
    [activeVideoId, related]
  );
  const relatedState = relatedLoading ? 'loading' : videoIdForFetch ? 'loaded' : 'idle';
  const canStartPlayback = useMemo(() => {
    if (serverPlaybackVerdict !== 'approved' || !serverApprovedVideo) return false;
    if (serverApprovedVideo.id !== videoIdRef.current) return false;
    return filterVideo(serverApprovedVideo, { allowlist, denylist, requireMadeForKids: true })
      .allowed;
  }, [allowlist, denylist, serverApprovedVideo, serverPlaybackVerdict]);

  useEffect(() => {
    relatedRef.current = related;
  }, [related]);

  useEffect(() => {
    watchedVideoIdsRef.current = watchedVideoIds;
  }, [watchedVideoIds]);

  const clearPendingAutoAdvance = useCallback(() => {
    if (!autoAdvanceTimeoutRef.current) return;
    clearTimeout(autoAdvanceTimeoutRef.current);
    autoAdvanceTimeoutRef.current = null;
  }, []);

  const findResumeSeconds = useCallback((targetVideoId: string): number => {
    const localResume = playbackResumeRef.current;
    if (localResume?.videoId === targetVideoId && localResume.seconds > 3) {
      return localResume.seconds;
    }

    const progress = getWatchProgressByVideoId(watchHistoryRef.current).get(targetVideoId);
    if (progress?.completed) return 0;

    let latestEntry = watchHistoryRef.current.find((entry) => entry.videoId === targetVideoId);
    for (const entry of watchHistoryRef.current) {
      if (entry.videoId !== targetVideoId) continue;
      if (!latestEntry || Date.parse(entry.watchedAt) > Date.parse(latestEntry.watchedAt)) {
        latestEntry = entry;
      }
    }

    return Math.max(0, Math.floor(latestEntry?.lastPositionSeconds || 0));
  }, []);

  useEffect(() => {
    const matchingIndex = related.findIndex((video) => video.id === activeVideoId);
    setCurrentIndex(matchingIndex >= 0 ? matchingIndex : 0);
  }, [activeVideoId, related]);

  const scheduleRelatedFetch = useCallback((nextVideoId: string) => {
    if (!nextVideoId) return;
    setVideoIdForFetch((current) => (current === nextVideoId ? current : nextVideoId));
  }, []);

  const enqueueCloudWatchSeconds = useCallback((targetVideoId: string, seconds: number) => {
    if (!targetVideoId || seconds <= 0) return;
    const previousSeconds = pendingCloudWatchSecondsRef.current.get(targetVideoId) || 0;
    pendingCloudWatchSecondsRef.current.set(targetVideoId, previousSeconds + seconds);
  }, []);

  const findVideoForCloudLog = useCallback(
    (targetVideoId: string) => {
      if (currentVideoRef.current?.id === targetVideoId) return currentVideoRef.current;
      return (
        relatedSource.find((video) => video.id === targetVideoId) ||
        discoveredVideos.find((video) => video.id === targetVideoId) ||
        null
      );
    },
    [discoveredVideos, relatedSource]
  );

  const flushCloudWatchEvent = useCallback(
    (targetVideoId: string) => {
      if (!targetVideoId || !isSignedIn) return;
      if (sendingCloudWatchRef.current.has(targetVideoId)) return;

      let batch = inFlightCloudWatchRef.current.get(targetVideoId);
      if (!batch) {
        const watchedDurationSeconds = pendingCloudWatchSecondsRef.current.get(targetVideoId) || 0;
        if (watchedDurationSeconds <= 0) return;

        const video = findVideoForCloudLog(targetVideoId);
        if (!video) return;

        let lastPositionSeconds = 0;
        try {
          const seconds = playerRef.current?.getCurrentTime?.();
          if (typeof seconds === 'number' && Number.isFinite(seconds) && seconds >= 0) {
            lastPositionSeconds = Math.floor(seconds);
          }
        } catch (e) {
          console.log('Cloud playback position read failed');
        }

        batch = {
          eventId: createWatchEventId(),
          payload: {
            profileId: currentProfile?.id || 'default',
            videoId: targetVideoId,
            videoTitle: video.snippet.title,
            channelId: video.snippet.channelId,
            channelTitle: video.snippet.channelTitle,
            thumbnailUrl:
              video.snippet.thumbnails.medium?.url || video.snippet.thumbnails.default?.url || '',
            watchedDurationSeconds,
            totalDurationSeconds: parseIso8601DurationSeconds(video.contentDetails?.duration),
            lastPositionSeconds,
          },
        };
        pendingCloudWatchSecondsRef.current.delete(targetVideoId);
        inFlightCloudWatchRef.current.set(targetVideoId, batch);
      }

      sendingCloudWatchRef.current.add(targetVideoId);
      fetch('/api/watch-history', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        keepalive: true,
        body: JSON.stringify({ eventId: batch.eventId, ...batch.payload }),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Watch sync failed with ${response.status}`);
          inFlightCloudWatchRef.current.delete(targetVideoId);
        })
        .catch(() => {
          // Keep the same event id for an idempotent retry. The server may have
          // committed even if this device lost the response.
          setTimeout(() => flushCloudWatchEventRef.current(targetVideoId), 5000);
        })
        .finally(() => {
          sendingCloudWatchRef.current.delete(targetVideoId);
          if (
            !inFlightCloudWatchRef.current.has(targetVideoId) &&
            (pendingCloudWatchSecondsRef.current.get(targetVideoId) || 0) > 0
          ) {
            queueMicrotask(() => flushCloudWatchEventRef.current(targetVideoId));
          }
        });
    },
    [currentProfile?.id, findVideoForCloudLog, isSignedIn]
  );

  useEffect(() => {
    flushCloudWatchEventRef.current = flushCloudWatchEvent;
  }, [flushCloudWatchEvent]);

  const recordWatchedVideo = useCallback(
    (video: Video | null) => {
      if (!video || watchedThisPlayerSessionRef.current.has(video.id)) return;

      watchedThisPlayerSessionRef.current.add(video.id);
      addToWatchHistory({
        profileId: currentProfile?.id || 'default',
        videoId: video.id,
        videoTitle: video.snippet.title,
        channelTitle: video.snippet.channelTitle,
        thumbnailUrl:
          video.snippet.thumbnails.medium?.url || video.snippet.thumbnails.default?.url || '',
        watchedDurationSeconds: 0,
        totalDurationSeconds: parseIso8601DurationSeconds(video.contentDetails?.duration),
      });
    },
    [addToWatchHistory, currentProfile?.id]
  );

  useEffect(() => {
    if (isPlaying) {
      recordWatchedVideo(currentVideo);
    } else {
      lastWatchTickAtRef.current = null;
    }
  }, [currentVideo, isPlaying, recordWatchedVideo]);

  useEffect(() => {
    const currentVideoId = videoIdRef.current || routeVideoId;
    if (!currentVideoId) return;

    scheduleRelatedFetch(currentVideoId);
  }, [routeVideoId, scheduleRelatedFetch]);

  // Do not even request the IFrame API until the Functions endpoint has
  // approved the active video. Navigation state is untrusted display data.
  useEffect(() => {
    if (canStartPlayback && !window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
    }
  }, [canStartPlayback]);

  // Show controls handler - respects dragging state
  const showControlsTemporarily = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    // Don't set hide timeout if user is actively dragging the related videos strip
    if (!isDraggingRef.current) {
      controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 4000);
    }
  }, []);

  useEffect(() => {
    showControlsTemporarily();
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [showControlsTemporarily]);

  // Drag handlers for related videos strip - prevent auto-hide while dragging
  const handleDragStart = useCallback(() => {
    isDraggingRef.current = true;
    // Cancel any pending hide timeout
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
      controlsTimeoutRef.current = null;
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    isDraggingRef.current = false;
    // Start the hide timer after user stops dragging
    controlsTimeoutRef.current = setTimeout(() => setShowControls(false), 4000);
  }, []);

  const refreshProgress = useCallback(() => {
    try {
      const current = playerRef.current?.getCurrentTime?.();
      const duration = playerRef.current?.getDuration?.();
      const fallbackDuration = parseIso8601DurationSeconds(currentVideo?.contentDetails?.duration);
      setProgress((previous) => ({
        current:
          typeof current === 'number' && Number.isFinite(current) && current >= 0
            ? current
            : previous.current,
        duration:
          typeof duration === 'number' && Number.isFinite(duration) && duration > 0
            ? duration
            : fallbackDuration,
      }));
    } catch (e) {
      const fallbackDuration = parseIso8601DurationSeconds(currentVideo?.contentDetails?.duration);
      setProgress((previous) => ({ current: previous.current, duration: fallbackDuration }));
    }
  }, [currentVideo]);

  // Fullscreen handler
  const enterMobileFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    const elem = containerRef.current;
    if (elem.requestFullscreen) {
      elem
        .requestFullscreen()
        .then(() => {
          if ('orientation' in screen && (screen.orientation as any).lock) {
            (screen.orientation as any).lock('landscape').catch(() => {});
          }
        })
        .catch(() => {});
    } else if ((elem as any).webkitRequestFullscreen) {
      (elem as any).webkitRequestFullscreen();
    }
  }, []);

  // Initialize or switch the YouTube player only after the server has
  // explicitly approved the current route video.
  useEffect(() => {
    const currentVideoId = serverApprovedVideo?.id;
    if (!canStartPlayback || !currentVideoId) return;
    let cancelled = false;

    const initPlayer = () => {
      if (cancelled || !canStartPlayback) return;
      if (!window.YT?.Player) {
        setTimeout(initPlayer, 100);
        return;
      }

      if (playerRef.current) {
        if (loadedVideoIdRef.current !== currentVideoId) {
          loadedVideoIdRef.current = currentVideoId;
          playerRef.current.loadVideoById?.(currentVideoId);
        }
        return;
      }

      const host = isSignedIn ? 'https://www.youtube.com' : 'https://www.youtube-nocookie.com';

      playerRef.current = new window.YT.Player('yt-player', {
        videoId: currentVideoId,
        host,
        playerVars: {
          autoplay: 1,
          mute: 1, // Start muted for iOS autoplay
          modestbranding: 1,
          rel: 0, // Don't show related videos
          fs: 1,
          playsinline: 1,
          controls: 0, // Hide YouTube controls (we have our own)
          cc_load_policy: 0,
          cc_lang_pref: '',
          iv_load_policy: 3, // Hide annotations
          disablekb: 1,
          showinfo: 0, // Hide video info (deprecated but still helps)
        },
        events: {
          onReady: (event: any) => {
            try {
              event.target.unloadModule?.('captions');
              event.target.unloadModule?.('cc');
              event.target.setOption?.('captions', 'track', {});
            } catch (e) {
              console.log('Caption disabling is not supported by this player');
            }
            scheduleRelatedFetch(currentVideoId);

            const resumeSeconds = findResumeSeconds(currentVideoId);
            if (resumeSeconds > 3) {
              try {
                event.target.seekTo(resumeSeconds, true);
              } catch (e) {
                console.log('Resume seek failed');
              }
            }

            // iOS FIX: Explicitly call playVideo() to ensure autoplay works
            // This is needed because iOS may not auto-start despite autoplay:1
            try {
              event.target.playVideo();
            } catch (e) {
              console.log('Play failed, user may need to tap');
            }

            // AUTO-UNMUTE: Since user clicked a video to get here, we have gesture
            // This works on iOS because the navigation was user-initiated
            if (!hasUnmutedRef.current) {
              setTimeout(() => {
                try {
                  event.target.unMute();
                  event.target.setVolume(100);
                  hasUnmutedRef.current = true;
                } catch (e) {
                  console.log('Unmute failed, user may need to tap');
                }
              }, 100);
            }
            // Only show controls on initial load; fullscreen stays behind a real tap/button gesture.
            if (isInitialLoadRef.current) {
              showControlsTemporarily();
              isInitialLoadRef.current = false;
            }
          },
          onStateChange: (e: any) => {
            // Track playing state for pause button
            if (e.data === 1) {
              // Playing
              clearPendingAutoAdvance();
              setIsPlaying(true);
              scheduleRelatedFetch(videoIdRef.current);
            } else if (e.data === 2 || e.data === 0) {
              // Paused or Ended
              flushCloudWatchEventRef.current(videoIdRef.current);
              setIsPlaying(false);
            }
            if (e.data === 0) {
              // Video ended
              const endedVideoId = videoIdRef.current;
              clearPlaybackPosition(endedVideoId);
              const latestRelated = relatedRef.current;
              const nextVideo = selectAdjacentVideo(
                latestRelated,
                endedVideoId,
                watchedVideoIdsRef.current,
                'next'
              );

              clearPendingAutoAdvance();
              autoAdvanceTimeoutRef.current = setTimeout(() => {
                autoAdvanceTimeoutRef.current = null;
                if (videoIdRef.current !== endedVideoId) return;

                if (nextVideo) {
                  const nextIndex = latestRelated.findIndex((video) => video.id === nextVideo.id);
                  switchToVideoInternalRef.current(nextVideo, nextIndex, {
                    saveCurrentPosition: false,
                  });
                } else {
                  navigate('/', { replace: true, state: { refreshHome: Date.now() } });
                }
              }, AUTO_ADVANCE_DELAY_MS);
            }
          },
          onError: (event: { data?: number }) => {
            const unavailableVideoId = videoIdRef.current;
            const errorCode = Number(event?.data || 0);
            console.warn('YouTube player error', { videoId: unavailableVideoId, errorCode });
            const latestRelated = relatedRef.current;
            const fallbackVideo = selectAdjacentVideo(
              latestRelated,
              unavailableVideoId,
              watchedVideoIdsRef.current,
              'next'
            );
            // Player/runtime errors are availability signals, not parental
            // policy. Never turn a transient HTML5/embed error into a durable
            // cross-device denylist entry or purge family history.
            if (fallbackVideo) {
              const nextIndex = latestRelated.findIndex((video) => video.id === fallbackVideo.id);
              switchToVideoInternalRef.current(fallbackVideo, nextIndex, {
                saveCurrentPosition: false,
              });
            } else {
              setPlaybackIssueReason(
                errorCode === 5 ? 'youtube_player_temporary_error' : 'youtube_player_unavailable'
              );
              setServerApprovedVideo(null);
              setServerPlaybackVerdict('retryable');
            }
          },
        },
      });
      loadedVideoIdRef.current = currentVideoId;
    };

    if (window.YT?.Player) initPlayer();
    else window.onYouTubeIframeAPIReady = initPlayer;

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canStartPlayback, isSignedIn, serverApprovedVideo?.id]);

  useEffect(() => {
    if (canStartPlayback || !playerRef.current) return;

    try {
      playerRef.current.stopVideo?.();
      playerRef.current.destroy?.();
    } catch (error) {
      console.log('Unable to stop unapproved video playback');
    }
    playerRef.current = null;
    loadedVideoIdRef.current = null;
    setIsPlaying(false);
  }, [canStartPlayback]);

  useEffect(() => {
    if (!isPlaying) return;
    const interval = setInterval(() => {
      const now = Date.now();
      const previousTickAt = lastWatchTickAtRef.current;
      const secondsToAdd = previousTickAt
        ? Math.max(1, Math.min(5, Math.round((now - previousTickAt) / 1000)))
        : 1;
      lastWatchTickAtRef.current = now;

      let totalDurationSeconds = parseIso8601DurationSeconds(
        currentVideoRef.current?.contentDetails?.duration
      );
      try {
        const playerDuration = playerRef.current?.getDuration?.();
        if (
          typeof playerDuration === 'number' &&
          Number.isFinite(playerDuration) &&
          playerDuration > 0
        ) {
          totalDurationSeconds = Math.floor(playerDuration);
        }
      } catch (e) {
        console.log('Playback duration read failed');
      }

      updateWatchHistoryDuration(videoIdRef.current, secondsToAdd, totalDurationSeconds);
      enqueueCloudWatchSeconds(videoIdRef.current, secondsToAdd);

      try {
        const seconds = playerRef.current?.getCurrentTime?.();
        if (typeof seconds === 'number') {
          savePlaybackPosition(videoIdRef.current, seconds, totalDurationSeconds);
        }
      } catch (e) {
        console.log('Playback position save failed');
      }
      refreshProgress();
    }, 1000);
    return () => clearInterval(interval);
  }, [
    enqueueCloudWatchSeconds,
    isPlaying,
    refreshProgress,
    savePlaybackPosition,
    updateWatchHistoryDuration,
  ]);

  useEffect(() => {
    if (!isPlaying || !isSignedIn) return;
    const interval = setInterval(() => {
      flushCloudWatchEventRef.current(videoIdRef.current);
    }, 30_000);
    return () => clearInterval(interval);
  }, [isPlaying, isSignedIn]);

  useEffect(() => {
    const interval = setInterval(refreshProgress, 1000);
    return () => clearInterval(interval);
  }, [refreshProgress]);

  useEffect(() => {
    refreshProgress();
  }, [currentVideo, refreshProgress]);

  useEffect(() => {
    return () => {
      clearPendingAutoAdvance();
      flushCloudWatchEventRef.current(videoIdRef.current);
      try {
        const seconds = playerRef.current?.getCurrentTime?.();
        if (typeof seconds === 'number') {
          savePlaybackPosition(
            videoIdRef.current,
            seconds,
            parseIso8601DurationSeconds(currentVideoRef.current?.contentDetails?.duration)
          );
        }
      } catch (e) {
        console.log('Playback position save failed');
      }
    };
  }, [clearPendingAutoAdvance, savePlaybackPosition]);

  // Internal video switch (uses loadVideoById for seamless transition)
  const switchToVideoInternal = useCallback(
    (video: Video, newIndex = -1, options: { saveCurrentPosition?: boolean } = {}) => {
      clearPendingAutoAdvance();
      console.log(
        '[DEBUG] switchToVideoInternal called with video:',
        video.id,
        'channelId:',
        video.snippet.channelId,
        'channelTitle:',
        video.snippet.channelTitle
      );
      const shouldSaveCurrentPosition = options.saveCurrentPosition !== false;

      if (shouldSaveCurrentPosition) {
        try {
          const seconds = playerRef.current?.getCurrentTime?.();
          if (typeof seconds === 'number') {
            savePlaybackPosition(
              videoIdRef.current,
              seconds,
              parseIso8601DurationSeconds(currentVideoRef.current?.contentDetails?.duration)
            );
          }
        } catch (e) {
          console.log('Playback position save failed');
        }
      } else {
        clearPlaybackPosition(videoIdRef.current);
      }
      flushCloudWatchEvent(videoIdRef.current);
      lastWatchTickAtRef.current = null;
      videoIdRef.current = video.id;
      // Related suggestions came from the already-approved server response.
      // Start them immediately and refresh their verdict in the background.
      setServerPlaybackVerdict('approved');
      setServerApprovedVideo(video);
      setCurrentVideo(video);
      setRelatedSource([]);
      navigate(`/watch/${video.id}`, {
        replace: true,
        state: {
          video,
          channel: {
            id: video.snippet.channelId,
            title: video.snippet.channelTitle,
          },
          returnTo: returnToRef.current,
        },
      });
      clearPlaybackPosition(video.id);

      const relatedIndex =
        newIndex >= 0
          ? newIndex
          : relatedRef.current.findIndex((relatedVideo) => relatedVideo.id === video.id);
      setCurrentIndex(relatedIndex >= 0 ? relatedIndex : 0);

      // Hide controls on switch
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
      setShowControls(false);

      const channelToSet = {
        id: video.snippet.channelId,
        title: video.snippet.channelTitle,
      };
      console.log('[DEBUG] switchToVideoInternal setting currentChannel to:', channelToSet);
      setCurrentChannel(channelToSet);

      scheduleRelatedFetch(video.id);
    },
    [
      clearPendingAutoAdvance,
      clearPlaybackPosition,
      flushCloudWatchEvent,
      navigate,
      savePlaybackPosition,
      scheduleRelatedFetch,
    ]
  );

  useEffect(() => {
    switchToVideoInternalRef.current = switchToVideoInternal;
  }, [switchToVideoInternal]);

  // Next/Prev handlers
  const goToNextVideo = useCallback(() => {
    const latestRelated = relatedRef.current;
    const nextVideo = selectAdjacentVideo(
      latestRelated,
      videoIdRef.current,
      watchedVideoIdsRef.current,
      'next'
    );
    if (!nextVideo) return;

    switchToVideoInternal(
      nextVideo,
      latestRelated.findIndex((video) => video.id === nextVideo.id)
    );
  }, [switchToVideoInternal]);

  const goToPreviousVideo = useCallback(() => {
    const latestRelated = relatedRef.current;
    const previousVideo = selectAdjacentVideo(
      latestRelated,
      videoIdRef.current,
      watchedVideoIdsRef.current,
      'previous'
    );
    if (!previousVideo) return;

    switchToVideoInternal(
      previousVideo,
      latestRelated.findIndex((video) => video.id === previousVideo.id)
    );
  }, [switchToVideoInternal]);

  // Channel actions
  const handleLikeChannel = () => {
    console.log('[DEBUG] handleLikeChannel triggered, currentChannel:', currentChannel);
    if (currentChannel) {
      console.log('[DEBUG] Calling addToAllowlist with:', {
        id: currentChannel.id,
        type: 'channel',
        title: currentChannel.title,
      });
      addToAllowlist({
        id: currentChannel.id,
        type: 'channel',
        title: currentChannel.title,
        addedAt: new Date().toISOString(),
        addedBy: 'user',
      });
      setMenuOpen(false);
    } else {
      console.log('[DEBUG] handleLikeChannel: currentChannel is null/undefined!');
    }
  };

  const handleBlockChannel = () => {
    console.log('[DEBUG] handleBlockChannel triggered, currentChannel:', currentChannel);
    if (currentChannel) {
      console.log('[DEBUG] Calling addToDenylist with:', {
        id: currentChannel.id,
        type: 'channel',
        title: currentChannel.title,
      });
      addToDenylist({
        id: currentChannel.id,
        type: 'channel',
        title: currentChannel.title,
        addedAt: new Date().toISOString(),
        addedBy: 'user',
      });
      setMenuOpen(false);
      navigate('/');
    } else {
      console.log('[DEBUG] handleBlockChannel: currentChannel is null/undefined!');
    }
  };

  // Effects: time check
  useEffect(() => {
    if (isTimeUp()) navigate('/');
  }, [isTimeUp, navigate]);

  // Effects: fetch related videos
  useEffect(() => {
    if (!videoIdForFetch) return;

    const controller = new AbortController();
    const cachedVideo = currentVideoRef.current;
    const hasCachedApproval =
      cachedVideo?.id === videoIdForFetch &&
      filterVideo(cachedVideo, { allowlist, denylist, requireMadeForKids: true }).allowed;

    setRelatedLoading(true);
    if (hasCachedApproval) {
      setPlaybackIssueReason(null);
      setServerPlaybackVerdict('approved');
      setServerApprovedVideo(cachedVideo);
    } else {
      setServerPlaybackVerdict('checking');
      setServerApprovedVideo(null);
    }

    fetch(`/api/related?videoId=${encodeURIComponent(videoIdForFetch)}`, {
      signal: controller.signal,
      credentials: 'include',
    })
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok && body.playbackVerdict !== 'retryable') {
          throw new Error(`Related request failed with ${r.status}`);
        }
        return body;
      })
      .then((d) => {
        // An aborted request may still resolve after a fast next/previous
        // navigation. Never let that stale verdict replace the active route.
        if (controller.signal.aborted || videoIdRef.current !== videoIdForFetch) return;

        const videos = (d.videos || []) as Video[];
        const apiCurrentVideo = (d.currentVideo || null) as Video | null;
        const verdict = d.playbackVerdict || (d.safeToPlay === true ? 'approved' : 'blocked');
        const isApproved =
          verdict === 'approved' &&
          d.safeToPlay === true &&
          apiCurrentVideo?.id === videoIdForFetch &&
          filterVideo(apiCurrentVideo, { allowlist, denylist, requireMadeForKids: true }).allowed;

        if (verdict === 'retryable') {
          setPlaybackIssueReason(d.reason || 'video_check_temporarily_unavailable');
          if (!hasCachedApproval) {
            setRelatedSource([]);
            setServerApprovedVideo(null);
            setServerPlaybackVerdict('retryable');
          }
          return;
        }

        if (!isApproved) {
          setPlaybackIssueReason(d.reason || 'content_blocked_or_unsafe');
          setRelatedSource([]);
          setServerApprovedVideo(null);
          setServerPlaybackVerdict('blocked');
          return;
        }

        setRelatedSource(videos);
        setPlaybackIssueReason(null);
        setServerApprovedVideo(apiCurrentVideo);
        setServerPlaybackVerdict('approved');
        setCurrentVideo(apiCurrentVideo);
        addDiscoveredVideos([apiCurrentVideo, ...videos]);
        setCurrentChannel({
          id: apiCurrentVideo.snippet.channelId,
          title: apiCurrentVideo.snippet.channelTitle,
        });
      })
      .catch((error) => {
        if (!controller.signal.aborted && error?.name !== 'AbortError' && !hasCachedApproval) {
          setPlaybackIssueReason('video_check_temporarily_unavailable');
          setRelatedSource([]);
          setServerApprovedVideo(null);
          setServerPlaybackVerdict('retryable');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setRelatedLoading(false);
        }
      });

    return () => controller.abort();
  }, [addDiscoveredVideos, allowlist, denylist, videoIdForFetch]);

  // Effects: sync route param
  useEffect(() => {
    if (routeVideoId && routeVideoId !== videoIdRef.current) {
      lastWatchTickAtRef.current = null;
      videoIdRef.current = routeVideoId;
      setRelatedSource([]);
      const cachedVideo =
        navState?.video?.id === routeVideoId ? navState.video : null;
      setServerPlaybackVerdict(cachedVideo ? 'approved' : 'checking');
      setServerApprovedVideo(cachedVideo);
      scheduleRelatedFetch(routeVideoId);
      setCurrentVideo(cachedVideo);
      if (navState?.channel) {
        setCurrentChannel(navState.channel);
      }
    }
  }, [navState?.channel, navState?.video, routeVideoId, scheduleRelatedFetch]);

  // Event handlers
  const handleBack = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearPendingAutoAdvance();
    const returnTo = returnToRef.current;
    navigate(returnTo, {
      replace: true,
      state: returnTo === '/' ? { refreshHome: Date.now() } : undefined,
    });
  };

  const handleRetryPlaybackCheck = () => {
    const targetVideoId = videoIdRef.current;
    setPlaybackIssueReason(null);
    setServerPlaybackVerdict(currentVideoRef.current ? 'approved' : 'checking');
    setVideoIdForFetch(null);
    queueMicrotask(() => setVideoIdForFetch(targetVideoId));
  };

  // Issue 7: Toggle controls on tap (show/hide)
  const handleVideoTap = () => {
    if (showControls) {
      setShowControls(false);
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    } else {
      showControlsTemporarily();
    }
  };

  // Issue 2: Tap to Start handler for iOS
  const handleTapToStart = () => {
    // Wait for player to be ready, then play
    const tryPlay = () => {
      if (playerRef.current && playerRef.current.playVideo) {
        try {
          playerRef.current.playVideo();
          playerRef.current.unMute();
          playerRef.current.setVolume(100);
          hasUnmutedRef.current = true;
          setIsPlaying(true);
        } catch (e) {
          console.log('Play/unmute failed');
        }
      } else {
        // Retry after short delay if player not ready
        setTimeout(tryPlay, 100);
      }
    };
    tryPlay();
    setShowTapToStart(false);
    enterMobileFullscreen();
  };

  // Pause/Play toggle handler
  const handlePlayPause = () => {
    if (!playerRef.current) return;
    try {
      if (isPlaying) {
        playerRef.current.pauseVideo();
      } else {
        playerRef.current.playVideo();
      }
    } catch (e) {
      console.log('Play/pause failed');
    }
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (progress.duration <= 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const nextTime = progress.duration * ratio;
    try {
      playerRef.current?.seekTo?.(nextTime, true);
    } catch (err) {
      console.log('Seek failed');
    }
    setProgress((current) => ({ ...current, current: nextTime }));
    savePlaybackPosition(videoIdRef.current, nextTime, progress.duration);
  };

  const progressPercent =
    progress.duration > 0
      ? Math.min(100, Math.max(0, (progress.current / progress.duration) * 100))
      : 0;

  if (serverPlaybackVerdict === 'blocked') {
    return (
      <div className="player-page player-page--safety" ref={containerRef}>
        <div className="player-safety-message" role="status">
          <div className="player-safety-message__icon">🛡️</div>
          <h1>This video is not available</h1>
          <p>Let’s choose something else to watch.</p>
          <button
            type="button"
            className="player-safety-message__back"
            onClick={() => navigate(returnToRef.current, { replace: true })}
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  if (serverPlaybackVerdict === 'retryable') {
    return (
      <div className="player-page player-page--safety" ref={containerRef}>
        <div className="player-safety-message" role="status" data-reason={playbackIssueReason || ''}>
          <div className="player-safety-message__icon">↻</div>
          <h1>We couldn’t load this video</h1>
          <p>The video service is temporarily unavailable. Please try again.</p>
          <button
            type="button"
            className="player-safety-message__back"
            onClick={handleRetryPlaybackCheck}
          >
            Try again
          </button>
          <button
            type="button"
            className="player-safety-message__back"
            onClick={() => navigate(returnToRef.current, { replace: true })}
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="player-page" ref={containerRef}>
      {/* Issue 2: Tap to Start overlay for iOS first video */}
      {showTapToStart && (
        <div className="player-tap-start" onClick={handleTapToStart}>
          <div className="player-tap-start__icon">▶</div>
          <p className="player-tap-start__text">Tap to Start</p>
        </div>
      )}

      {/* Main Video Area */}
      <div className="player-video-area">
        {/* YouTube Player container */}
        <div id="yt-player" className="player-iframe" />

        {/* Pause overlay - covers YouTube's "More videos" popup when paused */}
        {!isPlaying && !showControls ? (
          <div className="player-pause-overlay" onClick={handleVideoTap}>
            <div className="player-pause-icon">▶</div>
          </div>
        ) : (
          <div className="player-tap-overlay" onClick={handleVideoTap} />
        )}
      </div>

      {/* Controls Overlay */}
      <div className={`player-controls ${showControls ? 'visible' : ''}`}>
        {/* Back Button */}
        <button
          className={`player-float-btn player-btn--back ${showControls ? 'visible' : ''}`}
          onClick={handleBack}
        >
          ←
        </button>

        {/* Menu Button */}
        <button
          className={`player-float-btn player-btn--menu ${showControls ? 'visible' : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          ⋮
        </button>

        {/* Fullscreen Button */}
        <button
          className={`player-float-btn player-btn--fs ${showControls ? 'visible' : ''} ${showControls && visibleRelated.length > 0 ? 'has-related' : ''}`}
          onClick={enterMobileFullscreen}
        >
          ⛶
        </button>

        {/* Center Controls (Prev/Next/Pause) */}
        <div className={`player-center-controls ${showControls ? 'visible' : ''}`}>
          {visibleRelated.length > 0 && (
            <button className="player-mid-btn" onClick={goToPreviousVideo}>
              ⏮
            </button>
          )}
          <button
            className="player-mid-btn player-mid-btn--pause player-btn--play"
            onClick={handlePlayPause}
          >
            {isPlaying ? '⏸' : '▶'}
          </button>
          {visibleRelated.length > 0 && (
            <button className="player-mid-btn player-btn--next" onClick={goToNextVideo}>
              ⏭
            </button>
          )}
        </div>

        {/* Title Bar */}
        <div className={`player-title-bar ${showControls ? 'visible' : ''}`}>
          <div className="player-title">{currentVideo?.snippet.title || ''}</div>
          <div className="player-channel">
            {currentVideo?.snippet.channelTitle || currentChannel?.title || ''}
          </div>
        </div>
      </div>

      {/* Menu Dropdown */}
      {menuOpen && (
        <>
          <div className="player-menu-overlay" onClick={() => setMenuOpen(false)} />
          <div className="player-menu">
            <button onClick={handleLikeChannel}>❤️ Like Channel</button>
            <button className="danger" onClick={handleBlockChannel}>
              🚫 Block Channel
            </button>
          </div>
        </>
      )}

      {/* Related Videos Strip */}
      <div
        className={`player-related-container player-related ${showControls ? 'visible' : ''}`}
        data-related-state={relatedState}
        data-related-video-id={videoIdForFetch || ''}
      >
        {/* Progress Bar */}
        <button
          type="button"
          className="player-progress-container"
          onClick={handleProgressClick}
          aria-label="Video progress"
          aria-valuemin={0}
          aria-valuemax={Math.round(progress.duration)}
          aria-valuenow={Math.round(progress.current)}
          disabled={progress.duration <= 0}
        >
          <div className="player-progress-filled" style={{ width: `${progressPercent}%` }}>
            <div className="player-progress-handle" />
          </div>
        </button>

        {/* Scrollable Strip - drag events prevent auto-hide */}
        <div
          className="player-related-scroll"
          onTouchStart={handleDragStart}
          onTouchEnd={handleDragEnd}
          onMouseDown={handleDragStart}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
        >
          {relatedLoading && visibleRelated.length === 0 && (
            <div className="player-related-loading">Loading suggestions...</div>
          )}
          {visibleRelated.slice(0, 20).map((video) => {
            const watchState = getVideoWatchState(video.id, progressByVideoId);

            return (
              <button
                key={video.id}
                className="player-related-item"
                data-video-id={video.id}
                data-watch-state={watchState}
                onClick={() => switchToVideoInternal(video)}
              >
                <img
                  src={
                    video.snippet.thumbnails.medium?.url || video.snippet.thumbnails.default?.url
                  }
                  alt={video.snippet.title}
                  onError={(event) => applyFallbackThumbnail(event.currentTarget)}
                />
                <span className="player-related-duration">
                  {formatIso8601Duration(video.contentDetails?.duration)}
                </span>
                <span className="player-related-badge" aria-hidden="true">
                  {watchState === 'watched' ? '✓' : watchState === 'continue' ? '▶' : '★'}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
